/**
 * WebMcpProvider — creates and provides WebMcpContext to the app tree.
 *
 * Sits inside TevmContext.Provider so it has access to Tevm clients.
 * Reads enabled/profile from IndexedDB settings and exposes the context
 * value consumed by WebMcpPanel.
 *
 * Note: The full WebMcpServer (with @gotts.ai/tools handler integration)
 * is loaded lazily via dynamic import to avoid bundling Node-only deps
 * from the safe package into the browser build. When the dynamic import
 * fails (e.g. missing deps), webenv-only tools still work.
 */

import {
  ActivityLog,
  DEFAULT_WEBMCP_CONFIG,
  WebMcpContext,
  type WebMcpContextValue,
  type WebMcpProfile,
} from "@gotts.ai/mcp/browser";
import { TOOL_CATALOG } from "@gotts.ai/tools/tool-catalog";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getSetting, setSetting } from "../persistence/idb-store.js";
import { PROFILE_CATEGORIES } from "../webmcp/profiles.js";
import { CHAIN_CONTROL_TOOLS } from "../webmcp/tools/chain-controls.js";
import { ENVIRONMENT_TOOLS } from "../webmcp/tools/environment.js";
import { SCENARIO_TOOLS } from "../webmcp/tools/scenarios.js";
import { STATE_TOOLS } from "../webmcp/tools/state.js";

import { useRequiredTevmContext } from "./TevmContext.js";

import type { TevmClients } from "../webmcp/context.js";
import type { EnvironmentContext } from "../webmcp/tools/environment.js";
import type { ScenarioToolContext } from "../webmcp/tools/scenarios.js";
import type { StateContext } from "../webmcp/tools/state.js";
import type { ToolResult } from "@gotts.ai/mcp";

/** Lightweight tool registry that only includes webenv-specific tools. */
interface WebenvToolEntry {
  name: string;
  handler: (
    params: Record<string, unknown>,
  ) => ToolResult | Promise<ToolResult>;
}

function buildWebenvTools(
  clients: TevmClients,
  envCtx: EnvironmentContext,
  stateCtx: StateContext,
  scenarioCtx: ScenarioToolContext,
): WebenvToolEntry[] {
  const tools: WebenvToolEntry[] = [];

  for (const tool of ENVIRONMENT_TOOLS) {
    tools.push({
      name: tool.name,
      handler: (params) =>
        (
          tool.handler as (
            p: Record<string, unknown>,
            c: EnvironmentContext,
          ) => ToolResult | Promise<ToolResult>
        )(params, envCtx),
    });
  }

  for (const tool of CHAIN_CONTROL_TOOLS) {
    tools.push({
      name: tool.name,
      handler: (params) =>
        (
          tool.handler as (
            p: Record<string, unknown>,
            c: TevmClients,
          ) => ToolResult | Promise<ToolResult>
        )(params, clients),
    });
  }

  for (const tool of STATE_TOOLS) {
    tools.push({
      name: tool.name,
      handler: (params) =>
        (
          tool.handler as (
            p: Record<string, unknown>,
            c: StateContext,
          ) => ToolResult | Promise<ToolResult>
        )(params, stateCtx),
    });
  }

  for (const tool of SCENARIO_TOOLS) {
    tools.push({
      name: tool.name,
      handler: (params) =>
        (
          tool.handler as (
            p: Record<string, unknown>,
            c: ScenarioToolContext,
          ) => ToolResult | Promise<ToolResult>
        )(params, scenarioCtx),
    });
  }

  return tools;
}

export function WebMcpProvider({ children }: { children: React.ReactNode }) {
  const { publicClient, walletClient, testClient, deployment } =
    useRequiredTevmContext();

  const [enabled, setEnabledState] = useState(false);
  const [profile, setProfileState] = useState<WebMcpProfile>("webenv-full");
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const toolMapRef = useRef<Map<string, WebenvToolEntry>>(new Map());
  const logRef = useRef(new ActivityLog(DEFAULT_WEBMCP_CONFIG.maxLogEntries));

  // Load persisted settings on mount
  useEffect(() => {
    void (async () => {
      const [savedEnabled, savedProfile] = await Promise.allSettled([
        getSetting("webmcpEnabled"),
        getSetting("webmcpProfile"),
      ]);
      if (savedEnabled.status === "fulfilled") {
        setEnabledState(savedEnabled.value);
      }
      if (savedProfile.status === "fulfilled") {
        setProfileState(savedProfile.value as WebMcpProfile);
      }
      setSettingsLoaded(true);
    })();
  }, []);

  // Build webenv tools when enabled
  useEffect(() => {
    if (!settingsLoaded || !enabled) {
      toolMapRef.current = new Map();
      return;
    }

    const clients: TevmClients = { publicClient, walletClient, testClient };

    const envCtx: EnvironmentContext = {
      clients,
      getDeployment: () => deployment,
      deploy: () =>
        Promise.reject(new Error("Deploy not available via WebMCP")),
      reset: () => Promise.reject(new Error("Reset not available via WebMCP")),
      getForkInfo: () => null,
    };

    const stateCtx: StateContext = {
      clients,
      saveSnapshot: () =>
        Promise.reject(new Error("Snapshots not available via WebMCP yet")),
      restoreSnapshot: () =>
        Promise.reject(new Error("Snapshots not available via WebMCP yet")),
      listSnapshots: () => Promise.resolve([]),
      exportState: () =>
        Promise.reject(new Error("Export not available via WebMCP yet")),
      importState: () =>
        Promise.reject(new Error("Import not available via WebMCP yet")),
    };

    const scenarioCtx: ScenarioToolContext = {
      getScenarioContext: () => {
        if (!deployment) {
          throw new Error("Deployment not ready");
        }
        return { publicClient, walletClient, testClient, deployment };
      },
    };

    const tools = buildWebenvTools(clients, envCtx, stateCtx, scenarioCtx);
    const map = new Map<string, WebenvToolEntry>();
    for (const tool of tools) {
      map.set(tool.name, tool);
    }
    toolMapRef.current = map;
  }, [
    enabled,
    settingsLoaded,
    publicClient,
    walletClient,
    testClient,
    deployment,
  ]);

  // HMR bridge: handle tool calls from the Vite server plugin
  useEffect(() => {
    if (!import.meta.hot) return;

    const hot = import.meta.hot;

    // Notify server that browser is connected and ready
    hot.send("webmcp:connected", {});

    const handler = async (data: {
      callId: string;
      toolName: string;
      args: Record<string, unknown>;
    }) => {
      const tool = toolMapRef.current.get(data.toolName);
      if (!tool) {
        hot.send("webmcp:result", {
          callId: data.callId,
          error: `Unknown tool: ${data.toolName}`,
        });
        return;
      }

      try {
        const result = await tool.handler(data.args);
        hot.send("webmcp:result", {
          callId: data.callId,
          result,
        });
      } catch (err) {
        hot.send("webmcp:result", {
          callId: data.callId,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    };

    hot.on(
      "webmcp:call",
      (data: {
        callId: string;
        toolName: string;
        args: Record<string, unknown>;
      }) => {
        void handler(data);
      },
    );

    return () => {
      hot.send("webmcp:disconnected", {});
    };
  }, [enabled, settingsLoaded]);

  const setEnabled = useCallback((v: boolean) => {
    setEnabledState(v);
    void setSetting("webmcpEnabled", v);
  }, []);

  const setProfile = useCallback((p: WebMcpProfile) => {
    setProfileState(p);
    void setSetting("webmcpProfile", p);
  }, []);

  const value = useMemo<WebMcpContextValue>(
    () => ({
      isEnabled: enabled,
      setEnabled,
      profile,
      setProfile,
      async callTool(name, params) {
        const tool = toolMapRef.current.get(name);
        if (!tool) {
          return {
            content: [
              {
                type: "text" as const,
                text: JSON.stringify({ error: `Unknown tool: ${name}` }),
              },
            ],
            isError: true,
          };
        }
        const start = performance.now();
        try {
          const result = await tool.handler(params);
          logRef.current.addEntry({
            timestamp: Date.now(),
            clientId: "local",
            toolName: name,
            statusCode: result.isError ? "error" : "success",
            latencyMs: Math.round(performance.now() - start),
          });
          return result;
        } catch (err) {
          logRef.current.addEntry({
            timestamp: Date.now(),
            clientId: "local",
            toolName: name,
            statusCode: "error",
            latencyMs: Math.round(performance.now() - start),
          });
          return {
            content: [
              {
                type: "text" as const,
                text: JSON.stringify({
                  error: err instanceof Error ? err.message : String(err),
                }),
              },
            ],
            isError: true,
          };
        }
      },
      listTools: () => Array.from(toolMapRef.current.keys()).sort(),
      getToolCounts: () => {
        const webenv = toolMapRef.current.size;
        const allowed = PROFILE_CATEGORIES[profile];
        const safe = TOOL_CATALOG.filter((t) => allowed.has(t.category)).length;
        return { safe, webenv, total: safe + webenv };
      },
      getLogEntries: (limit) => logRef.current.getEntries(limit),
      clearLog: () => {
        logRef.current.clear();
      },
      wsUrl: `${globalThis.location.protocol === "https:" ? "wss:" : "ws:"}//${globalThis.location.host}/mcp`,
      httpUrl: `${globalThis.location.origin}/mcp`,
    }),
    [enabled, setEnabled, profile, setProfile],
  );

  return (
    <WebMcpContext.Provider value={value}>{children}</WebMcpContext.Provider>
  );
}
